import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { login as apiLogin, signup as apiSignup, logout as apiLogout, getCurrentUser, getCurrentUserByCookie, loginWithGoogle as apiLoginWithGoogle } from './api/auth'
import type { User } from './types/api'

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  loading: boolean
  login: (email: string, password: string) => Promise<void>
  loginWithGoogle: (googleIdToken: string) => Promise<void>
  signup: (name: string, email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  const refreshUser = useCallback(async () => {
    try {
      let currentUser: User | null = null

      const hasStoredToken =
        typeof window !== 'undefined' && !!window.sessionStorage.getItem('accessToken')

      // 1) If we already have a token locally, prefer the normal /auth/me path.
      if (hasStoredToken) {
        currentUser = await getCurrentUser()
        if (currentUser) {
          setUser(currentUser)
          return
        }
        // If /auth/me fails, we don't immediately clear local user; we fall through and
        // optionally try the cookie bootstrap if there is no token.
      }

      // 2) If we have no local token, try to bootstrap a session from the shared cookie.
      if (!hasStoredToken) {
        const byCookie = await getCurrentUserByCookie()
        if (byCookie?.user) {
          currentUser = byCookie.user
          if (typeof window !== 'undefined') {
            window.localStorage.setItem('user', JSON.stringify(byCookie.user))
            if (byCookie.accessToken) sessionStorage.setItem('accessToken', byCookie.accessToken)
            if (byCookie.refreshToken) sessionStorage.setItem('refreshToken', byCookie.refreshToken)
          }
        }
      }

      // 3) Apply result. Only clear when we truly have no local reference.
      if (currentUser) {
        setUser(currentUser)
      } else if (typeof window !== 'undefined' && !window.localStorage.getItem('user')) {
        setUser(null)
      }
    } catch (error) {
      // On hard errors, keep any cached user; only clear when there's nothing stored.
      if (typeof window !== 'undefined' && !window.localStorage.getItem('user')) {
        setUser(null)
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    // Hydrate from sessionStorage after first paint so the app-wide loading
    // screen is visible and we avoid flashing logged-in content.
    if (typeof window !== 'undefined') {
      // For UX, prefer cached user in localStorage so we can instantly show \"Welcome back\"
      const cached = window.localStorage.getItem('user')
      if (cached) {
        try {
          const parsed = JSON.parse(cached) as User
          setUser(parsed)
        } catch {
          // ignore parse errors and fall back to backend check
        }
      }
      // Defer setLoading(false) so AppLoadingScreen paints before any route content
      requestAnimationFrame(() => {
        setLoading(false)
      })
    }
    refreshUser()
  }, [refreshUser])

  const login = useCallback(async (email: string, password: string) => {
    try {
      setLoading(true)
      const response = await apiLogin(email, password)
      setUser(response.user)
      // Persist user in localStorage for UX, tokens in sessionStorage for API calls
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('user', JSON.stringify(response.user))
        if (response.accessToken) sessionStorage.setItem('accessToken', response.accessToken)
        if (response.refreshToken) sessionStorage.setItem('refreshToken', response.refreshToken)
      }
    } catch (error) {
      setUser(null)
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  const loginWithGoogle = useCallback(async (googleIdToken: string) => {
    try {
      setLoading(true)
      const response = await apiLoginWithGoogle(googleIdToken)
      setUser(response.user)
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('user', JSON.stringify(response.user))
        if (response.accessToken) sessionStorage.setItem('accessToken', response.accessToken)
        if (response.refreshToken) sessionStorage.setItem('refreshToken', response.refreshToken)
      }
    } catch (error) {
      setUser(null)
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  const signup = useCallback(async (name: string, email: string, password: string) => {
    try {
      setLoading(true)
      const response = await apiSignup(name, email, password)
      setUser(response.user)

      // Persist user in localStorage for UX
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('user', JSON.stringify(response.user))
      }
    } catch (error) {
      setUser(null)
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  const logout = useCallback(async () => {
    try {
      await apiLogout()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      setUser(null)
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem('user')
        sessionStorage.removeItem('accessToken')
        sessionStorage.removeItem('refreshToken')
      }
    }
  }, [])

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    loading,
    login,
    loginWithGoogle,
    signup,
    logout,
    refreshUser,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
